import { ArrowRight } from './Icons';
import { useTranslation } from '../contexts/TranslationContext';
import { LANDING_COVERAGE_COUNTRIES } from '../data/landingCoverageCountries';

interface RegulationSourcesListProps {
  className?: string;
}

const REGULATION_SOURCE_COUNTRIES = [...LANDING_COVERAGE_COUNTRIES].sort((first, second) => {
  if (first.status === second.status) return 0;
  return first.status === 'integrated' ? -1 : 1;
});

export function RegulationSourcesList({ className = '' }: RegulationSourcesListProps) {
  const { language, t } = useTranslation();
  const dateFormatter = new Intl.DateTimeFormat(language === 'fr' ? 'fr-FR' : 'en-GB', {
    dateStyle: 'long',
    timeZone: 'UTC',
  });

  return (
    <dl
      data-regulation-sources-list="true"
      className={`grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-6 text-left ${className}`}
      aria-label={t('coverageMap.countriesLabel')}
    >
      {REGULATION_SOURCE_COUNTRIES.map((country) => {
        const isPending = country.status === 'pending';
        const hasSingleRegulation = country.regulations.length === 1;

        return (
          <div
            key={country.code}
            data-coverage-country-description={country.code}
            data-coverage-country-status={country.status}
            className="contents"
          >
            <dt
              className={`flex min-w-0 items-center gap-2 text-sm font-semibold leading-tight theme-coverage-regulation-name ${hasSingleRegulation || isPending ? 'self-center' : 'self-start pt-0.5'}`}
              data-coverage-country-label={country.code}
              aria-label={t(country.labelKey)}
            >
              <span className="text-2xl leading-none" aria-hidden="true">{country.flag}</span>
              <span className={isPending ? 'inline' : 'hidden md:inline'} data-coverage-country-name={country.code}>
                {t(country.labelKey)}
              </span>
            </dt>
            <dd className="min-w-0">
              {isPending ? (
                <p className="self-center text-sm italic leading-tight theme-coverage-description" data-coverage-pending={country.code}>
                  — {t('coverageMap.pendingIntegration')}
                </p>
              ) : (
                <ul className="space-y-2 text-xs leading-relaxed">
                  {country.regulations.map((regulation) => (
                    <li key={regulation.href} className="theme-coverage-description">
                      <a
                        className="group/regulation inline-flex items-start gap-1.5 underline-offset-4 hover:underline focus-visible:rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                        href={regulation.href}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <ArrowRight className="theme-coverage-regulation-arrow mt-0.5 h-3.5 w-3.5 shrink-0 transition-transform group-hover/regulation:translate-x-0.5 motion-reduce:transition-none" />
                        <span>
                          {regulation.descriptionKey ? (
                            <>
                              <strong className="theme-coverage-regulation-name">{t(regulation.nameKey)}</strong>
                              <span className="theme-coverage-regulation-description">{`\u00a0: ${t(regulation.descriptionKey)}`}</span>
                            </>
                          ) : (
                            <strong className="theme-coverage-regulation-name">{t(regulation.nameKey)}</strong>
                          )}
                          {regulation.consolidatedAt && (
                            <span className="theme-coverage-description block">
                              {t('coverageMap.consolidatedOn')}{' '}
                              <time dateTime={regulation.consolidatedAt}>
                                {dateFormatter.format(new Date(`${regulation.consolidatedAt}T00:00:00Z`))}
                              </time>
                            </span>
                          )}
                        </span>
                      </a>
                    </li>
                  ))}
                </ul>
              )}
            </dd>
          </div>
        );
      })}
    </dl>
  );
}
